import { Observable, of } from 'rxjs'
import { fromPromise } from 'rxjs/internal-compatibility'
import * as moment from 'moment'
import { ClientConfig } from '../../config/ClientConfig'

import { PokerMap } from '../../data/Map'
import { myPlayer } from '../../MyPlayer'
import { _noop, TimedCache } from '../../script-utils'
import { HttpService } from '../../services/http-service'
import { AuthService } from '../../auth/AuthService'
import { LeaderboardDTO, PlayerLeaderboardPositionDTO } from '../OperatorPublicApi'
import {
	BonusInstanceResponse,
	BonusStatus,
	BuyProductResponse,
	HandHistoryList,
	HistoricHand,
	HistoricHandDetailResponse,
	PlayerAchievementList,
	PlayerItemList,
	PlayerNote,
	PlayerNoteList,
	PlayerProfile,
	ProductList,
	ShareResponse,
	TimeoutType,
	UserTimeoutDTO,
} from '../PlayerApiProtocol.generated'
import { FeatureToggleService } from '../../services/FeatureToggleService'
import { isLeaderboardPositionResponse, LeaderboardsApiService } from '../../services/LeaderboardApiService'
import { Feature, userFeatureService } from '../../services/UserFeatureService'

export const LEADERBOARD_IDS_CACHE_KEY = 'leaderboard_ids'

export const PlayerApi = new (class {
	leaderboardIdsCache = new TimedCache<string[]>(300_000)
	leaderboardByIdCache = new PokerMap<any>()
	positionCache = new TimedCache<any>(60000)
	newSystemLeaderboardIds: string[] = []

	requestTransactions(
		playerId: number,
		page: number,
		type: string,
		currency: string,
		onSuccess: SuccessCallback<any>,
		onError: ErrorCallback,
	) {
		const pageString = page == undefined ? '' : `&page=${page}`
		const currencyString = currency == undefined ? '' : `&currency=${currency}`
		const typeString = type == undefined ? '' : `&type=${type}`
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/transactions?1=1${pageString}${typeString}${currencyString}`

		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	buyProduct(productId: string, onSuccess: SuccessCallback<BuyProductResponse>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/product/buy/${productId}`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	requestProducts(onSuccess: SuccessCallback<ProductList>) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/product/list/10`
		HttpService.get(url, { authenticate: true }).then(onSuccess)
	}

	requestBonusInstances(onSuccess: SuccessCallback<BonusInstanceResponse[]>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/bonus/list/active`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	shareHand(handId: string, onSuccess: SuccessCallback<ShareResponse>, onError: ErrorCallback) {
		AuthService.getCurrentUserId().then((userId) => {
			const url = `${ClientConfig.POKER_TABLE_URL}/table-data/player/v1/hand-history/user/${userId}/hand/${handId}/share`
			HttpService.post(url, {}, { authenticate: true })
				.then((response: string) => {
					const shareResponse: ShareResponse = {
						sharedHistoricHandId: response,
					}
					onSuccess(shareResponse)
				})
				.catch(onError)
		})
	}

	getSharedHand(sharedHandId, prefix, onSuccess: SuccessCallback<HistoricHand>) {
		const url = `${ClientConfig.POKER_TABLE_URL}/table-data/player/v1/hand-history/hands/shared/${sharedHandId}?format=HistoricHand`
		HttpService.get(url).then((response: { hand: HistoricHand }) => {
			onSuccess(response.hand)
		})
	}

	updateAvatar(avatarId: string, onSuccess: SuccessCallback<void>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/avatar/${avatarId}`
		HttpService.put(url, {}, { authenticate: true }).then(onSuccess).catch(onError)
	}

	equipItem(itemId: string, onSuccess: SuccessCallback<void>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/item/equip/${itemId}`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	unequipItem(itemId: string, onSuccess: SuccessCallback<void>, onError?: ErrorCallback) {
		const error = onError || _noop
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/item/unequip/${itemId}`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(error)
	}

	requestPrivatePlayerProfile(onSuccess: SuccessCallback<PlayerProfile>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/profile`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	savePlayerNote(note: PlayerNote, onSuccess: () => void, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/notes?playerId=${myPlayer.id}`
		HttpService.put(url, note, { authenticate: true }).then(onSuccess).catch(onError)
	}

	requestPlayerNotes(playerIds: number[], onSuccess: SuccessCallback<PlayerNoteList>, onError: ErrorCallback = _noop) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/notes`
		HttpService.post(url, { players: playerIds }, { authenticate: true }).then(onSuccess).catch(onError)
	}

	requestAchievements(onSuccess: SuccessCallback<PlayerAchievementList>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/achievements/poker?operatorId=10`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	requestItems(onSuccess: SuccessCallback<PlayerItemList>, onError?: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/items?operatorId=10`
		HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	requestBonusInfo(onSuccess: SuccessCallback<BonusStatus>, onError: ErrorCallback) {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/bonus`
		return HttpService.get(url, { authenticate: true }).then(onSuccess).catch(onError)
	}

	async getTableServerHandHistory(
		currency: string,
		count: number,
		startTime: number,
		tableIntegrationId: string,
		descending: boolean,
		onSuccess: SuccessCallback<HandHistoryList>,
		onError: ErrorCallback,
	) {
		const playerId = await AuthService.getCurrentUserId()
		const tableParam = tableIntegrationId ? `&tableIntegrationId=${tableIntegrationId}` : ''
		const endTimeParam = startTime !== 0 ? `&endTime=${moment(startTime).toISOString()}` : ''
		const coinType = currency === 'XGC' ? 'Gold' : 'Sweeps'

		const url = `${ClientConfig.POKER_TABLE_URL}/table-data/player/v1/hand-history/${playerId}/hands?format=HistoricHand${tableParam}&coinType=${coinType}${endTimeParam}`

		HttpService.get(url, { authenticate: true })
			.then((hands: HistoricHand[]) =>
				onSuccess({
					oldestHandStartTime: moment().subtract(30, 'days').valueOf(),
					latestHandStartTime: moment().valueOf(),
					hands: hands,
				}),
			)
			.catch(onError)
	}

	async getTableServerHandHistoryDetail(
		handId: string,
		onSuccess: SuccessCallback<HistoricHand>,
		onError: ErrorCallback,
	) {
		const playerId = await AuthService.getCurrentUserId()
		const url = `${ClientConfig.POKER_TABLE_URL}/table-data/player/v1/hand-history/${playerId}/hands/${handId}?format=HistoricHand`
		HttpService.get(url, { authenticate: true })
			.then((response: HistoricHandDetailResponse) => onSuccess(response.hand))
			.catch(onError)
	}

	requestAvailableLeaderboardIds(): Observable<string[]> {
		const cached = this.leaderboardIdsCache.get(LEADERBOARD_IDS_CACHE_KEY)
		if (cached) {
			return of(cached)
		}

		return fromPromise(
			LeaderboardsApiService.getGameClientLeaderboards().then((res: LeaderboardDTO[]) => {
				const leaderboardIds = res.filter(x => userFeatureService.doesUserHaveFeature(Feature.SWEEPS_LEADERBOARDS)
						? true
						: x.currency !== 'Sweeps'
					)
					.map(x => x.id)

				this.newSystemLeaderboardIds = leaderboardIds
				this.leaderboardIdsCache.put(LEADERBOARD_IDS_CACHE_KEY, leaderboardIds)
				return leaderboardIds
			})
		)
	}

	requestLeaderboardWithCount(
		leaderboardId: string,
		count: number,
		onSuccess: SuccessCallback<any>,
		onError: ErrorCallback,
	) {
		const cacheKey = leaderboardId + count
		const cached = this.leaderboardByIdCache.get(cacheKey)
		const now = Date.now()

		if (cached && cached.time > now - 60 * 1000) {
			onSuccess(cached.data)
			return
		}
		if (cached) {
			this.leaderboardByIdCache.remove(cacheKey)
		}

		if (this.newSystemLeaderboardIds.includes(leaderboardId)) {
			LeaderboardsApiService.getLeaderboardPositions(leaderboardId)
				.then((res) => {
					const mappedEntries = res.entries.map((entry) => {
						return {
							playerId: entry.userId.toString(),
							screenName: entry.screenName,
							value: entry.points,
						}
					})
					const mappedRes: LeaderboardDTO = {
						id: res.leaderboard.id,
						name: res.leaderboard.name,
						description: res.leaderboard.description,
						periodStart: res.leaderboard.periodStart,
						periodEnd: res.leaderboard.periodEnd,
						entries: mappedEntries,
					}
					this.leaderboardByIdCache.put(cacheKey, { data: mappedRes, time: now })
					return mappedRes
				})
				.then(onSuccess)
				.catch(onError)
		} else {
			LeaderboardsApiService.getGameClientLeaderboards()
				.then((leaderboards: LeaderboardDTO[]) => {
					const leaderboard = leaderboards.find(lb => lb.id === leaderboardId)
					if (leaderboard && leaderboard.entries && count) {
						leaderboard.entries = leaderboard.entries.slice(0, count)
					}
					this.leaderboardByIdCache.put(cacheKey, { data: leaderboard, time: now })
					return leaderboard
				})
				.then(onSuccess)
				.catch(onError)
		}
	}

	requestMyLeaderboardPosition(leaderboardId: string): Observable<PlayerLeaderboardPositionDTO> {
		const cached = this.positionCache.get(leaderboardId)
		if (cached) {
			return of(cached)
		}

		const playerPosition = AuthService.getCurrentUserId().then(userId => {
			return LeaderboardsApiService
				.getMyLeaderboardPosition(leaderboardId, Number(userId))
				.then(res => {
					const mappedRes: PlayerLeaderboardPositionDTO = isLeaderboardPositionResponse(res)
						? {
							player: {
								playerId: res.userId.toString(),
								screenName: res.screenName,
								value: res.points,
							},
							position: res.position - 1,
						}
						: {
							player: {
								playerId: userId,
								screenName: '',
								value: 0,
							},
							position: -1,
						}

					this.positionCache.put(leaderboardId, mappedRes)
					return mappedRes
				})
		})

		return fromPromise(playerPosition)
	}


	requestMultipleLeaderboardsWithoutEntries(leaderboardIds: string[]): Observable<LeaderboardDTO[]> {
		return fromPromise(
			LeaderboardsApiService.getGameClientLeaderboards().then((gamificationLeaderboards: LeaderboardDTO[]) => {
				return userFeatureService.doesUserHaveFeature(Feature.SWEEPS_LEADERBOARDS) ?
					gamificationLeaderboards
					: gamificationLeaderboards.filter(leaderboard => leaderboard.currency !== 'Sweeps')
			})
		)
	}

	requestUserTimeout(period: string, timeoutType?: TimeoutType, endDate?: Date): Observable<UserTimeoutDTO> {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/setTimeout`

		return fromPromise(HttpService.post(url, { timeoutType, period, endDate }, { authenticate: true }))
	}

	fetchUserTimeout(): Observable<UserTimeoutDTO> {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/getTimeout`

		return fromPromise(HttpService.get(url, { authenticate: true }))
	}

	fetchUserTimeouts(): Observable<UserTimeoutDTO[]> {
		const url = `${ClientConfig.USERAPI_BASE_URL}/player/getActiveTimeouts`

		return fromPromise(HttpService.get(url, { authenticate: true }))
	}
})()

type SuccessCallback<T> = (response: T) => void
type ErrorCallback = () => void
